<template>
  <Widget class="fill-height">
    <v-card style="border-radius: 6px" flat class="fill-height">
      <div class="text-left">
        <v-card-text class="pa-6">
          <h2 class="text-left mb-2">Questionnaire responses</h2>

          <v-table
            v-if="questionnaireResponses.length"
            density="compact"
            class="ml-1"
          >
            <thead>
              <tr>
                <th>Questionnaire</th>
                <th>Received from</th>
                <th>Status</th>
                <th>Last updated</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="response in completedQuestionnaireResponses">
                <th>{{ response.questionnaireLabel }}</th>
                <th>{{ response.senderCompanyName }}</th>
                <th>{{ answerStatusToDisplayText(response.answerStatus) }}</th>
                <th>{{ getLastEditedString(response) }}</th>
                <th>
                  <AppkitIcon
                    icon="icon-document-text-outline"
                    class="report-icon"
                    @click="goToReportView(response)"
                  />
                </th>
              </tr>
            </tbody>
          </v-table>
          <v-row v-else class="mt-2">
            <v-col>
              The company has not responded to any questionnaires.
            </v-col>
          </v-row>
        </v-card-text>
      </div>
    </v-card>
  </Widget>
</template>

<script setup lang="ts">
import Widget from "@/views/dashboard-view/Widget.vue";
import { useUnitProfileStore } from "@/stores/unitProfileStore";
import {
  AnswerStatus,
  answerStatusToDisplayText,
} from "@/models/enum/answerStatus";
import { useReportViewStore } from "@/stores/reportViewStore";
import { RouterPath } from "@/models/enum/routerPath";
import { useRouter } from "vue-router";
import { computed } from "vue";
import AppkitIcon from "@/components/custom/AppkitIcon.vue";
import type { QuestionnaireResponseSummary } from "@/models/questionnaireResponseSummary";

const router = useRouter();
const unitProfileStore = useUnitProfileStore();
const reportViewStore = useReportViewStore();
const questionnaireResponses =
  unitProfileStore.selectedUnitProfile.questionnaireResponses;

const completedQuestionnaireResponses = computed(() =>
  questionnaireResponses.filter((q) => q.answerStatus == AnswerStatus.Completed)
);

function goToReportView(response: QuestionnaireResponseSummary): void {
  reportViewStore.selectedQuestionnareId = response.questionnaireId;
  reportViewStore.selectedContextId = response.contextId;
  reportViewStore.selectedQuestionnaireLabel = response.questionnaireLabel;
  reportViewStore.selectedReceiverCompanyName =
    unitProfileStore.selectedUnitProfile.basicCompanyInfo.companyName;
  router.push(RouterPath.QuestionnaireAnswers);
}

function getLastEditedString(response: QuestionnaireResponseSummary): string {
  if (response.lastEdited == null) {
    return "";
  } else {
    let lastEdited = new Date(response.lastEdited);
    return `${lastEdited.getDate().toString().padStart(2, "0")}.${(
      lastEdited.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}.${lastEdited.getFullYear()}, ${lastEdited
      .getHours()
      .toString()
      .padStart(2, "0")}:${lastEdited
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  }
}
</script>

<style scoped lang="scss">
.report-icon {
  cursor: pointer;
}
</style>
